import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  PhoneIcon,
  DocumentCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';

const SubscriptionRequestPage = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentProof, setPaymentProof] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const location = useLocation();

  const loadSubscriptionPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/subscription-plans');
      if (response.data.plans && response.data.plans.length > 0) {
        setPlans(response.data.plans);
        const requestedPlan = new URLSearchParams(location.search).get('plan');
        const selectedPlan = requestedPlan
          ? response.data.plans.find((plan) => String(plan.code) === requestedPlan || String(plan.id) === requestedPlan)
          : null;
        setSelectedPackageId((selectedPlan || response.data.plans[0]).id);
      }
    } catch (error) {
      console.error('Failed to load subscription plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    loadSubscriptionPlans();
  }, [loadSubscriptionPlans]);

  const selectedPlan = plans.find(p => p.id === selectedPackageId);

  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    return selectedPlan.price * selectedMonths;
  };

  const handleRequestSubscription = async () => {
    try {
      setSaving(true);
      
      if (!selectedPlan) {
        toast.error('Please select a subscription plan');
        return;
      }

      // ✅ DON'T save to database yet - just prepare data for upload
      // Only show payment modal - user must upload proof first
      toast.success('✅ Ready to proceed! Please upload your payment proof.');
      
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF) or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setPaymentProof(file);
  };

  const handleSubmitPaymentProof = async () => {
    try {
      if (!paymentProof) {
        toast.error('Please select a payment proof file');
        return;
      }

      if (!selectedPlan) {
        toast.error('Please select a subscription plan');
        return;
      }

      setUploadingProof(true);

      const formData = new FormData();
      formData.append('paymentProof', paymentProof);
      // ✅ Send package and months along with proof
      formData.append('package', selectedPlan.code);
      formData.append('months', selectedMonths);
      formData.append('totalPrice', calculateTotal());

      await apiClient.post('/auth/upload-payment-proof', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('✅ Payment proof uploaded successfully! Admin will review and activate your subscription within 1-2 hours.');
      setShowPaymentModal(false);
      setPaymentProof(null);
      
      // Reset form
      setSelectedPackageId(plans.length > 0 ? plans[0].id : null);
      setSelectedMonths(1);
    } catch (error) {
      console.error('Failed to upload payment proof:', error);
      toast.error(error.response?.data?.message || 'Failed to upload payment proof');
    } finally {
      setUploadingProof(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Subscription Plan
          </h1>
          <p className="text-xl text-gray-600">
            Select a package and subscription duration that fits your needs
          </p>
        </div>

        {/* Duration Selector */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📅 Subscription Duration</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {[1, 2, 3, 6, 9, 12].map((months) => (
              <button
                key={months}
                onClick={() => setSelectedMonths(months)}
                className={`py-3 rounded-lg font-medium transition-all ${
                  selectedMonths === months
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {months} {months === 1 ? 'Month' : 'Months'}
              </button>
            ))}
          </div>
        </div>

        {/* Package Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {plans.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackageId(pkg.id)}
              className={`rounded-lg shadow-lg overflow-hidden cursor-pointer transition-all transform hover:scale-105 ${
                selectedPackageId === pkg.id
                  ? 'ring-4 ring-blue-500 bg-blue-50'
                  : 'bg-white hover:shadow-xl'
              }`}
            >
              <div className="p-6">
                {/* Package Name */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      PKR {pkg.price.toLocaleString()}
                    </span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Total: <span className="font-semibold text-blue-600">
                      PKR {(pkg.price * selectedMonths).toLocaleString()}
                    </span>
                  </p>
                </div>

                {/* Invoice Limit */}
                <div className="bg-blue-50 rounded p-3 mb-4">
                  <p className="text-sm font-medium text-gray-700">
                    {pkg.max_invoices_per_month} invoices/month
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {(pkg.features || []).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Select Button */}
                <button
                  onClick={() => setSelectedPackageId(pkg.id)}
                  className={`w-full py-2 rounded-lg font-medium transition-colors ${
                    selectedPackageId === pkg.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {selectedPackageId === pkg.id ? '✓ Selected' : 'Select'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Summary */}
            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-700">Package:</span>
                  <span className="font-semibold text-gray-900">{selectedPlan?.name || 'Select a plan'}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-700">Duration:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedMonths} {selectedMonths === 1 ? 'Month' : 'Months'}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-700">Monthly Price:</span>
                  <span className="font-semibold text-gray-900">
                    PKR {selectedPlan?.price?.toLocaleString() || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 text-lg">
                  <span className="text-gray-900 font-bold">Total Amount:</span>
                  <span className="text-3xl font-bold text-blue-600">
                    PKR {calculateTotal().toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Action */}
            <div className="flex flex-col justify-between">
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <CreditCardIcon className="h-5 w-5 mr-2 text-blue-600" />
                  <span>Fast & Secure</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600" />
                  <span>Instant Activation</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentCheckIcon className="h-5 w-5 mr-2 text-purple-600" />
                  <span>Invoice Provided</span>
                </div>
              </div>

              <button
                onClick={handleRequestSubscription}
                disabled={saving}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Proceed to Payment
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">💳 Payment Instructions</h2>

              {/* Order Details */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Package</p>
                    <p className="font-bold text-gray-900">{selectedPlan?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-bold text-gray-900">{selectedMonths} Month(s)</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="font-bold text-blue-600 text-lg">PKR {calculateTotal().toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-bold text-orange-600">Pending Payment</p>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900">Choose Payment Method:</h3>

                {/* EasyPaisa */}
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">🟦 EasyPaisa</h4>
                      <div className="text-sm">
                        <p className="text-gray-700"><strong>Number:</strong> 03000047478</p>
                        <p className="text-gray-700"><strong>Name:</strong> Muhammad Yousaf Iqbal</p>
                        <p className="text-gray-700"><strong>Amount:</strong> PKR {calculateTotal().toLocaleString()}</p>
                      </div>
                    </div>
                    <CreditCardIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>

                {/* Jazz Cash */}
                <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">🟪 Jazz Cash</h4>
                      <div className="text-sm">
                        <p className="text-gray-700"><strong>Number:</strong> 03000047478</p>
                        <p className="text-gray-700"><strong>Name:</strong> Muhammad Yousaf Iqbal</p>
                        <p className="text-gray-700"><strong>Amount:</strong> PKR {calculateTotal().toLocaleString()}</p>
                      </div>
                    </div>
                    <CreditCardIcon className="h-6 w-6 text-purple-600" />
                  </div>
                </div>

                {/* Bank Rast */}
                <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">🏦 Standard Chartered Bank Pakistan</h4>
                      <div className="text-sm">
                        <p className="text-gray-700"><strong>Rasst ID:</strong> 03000047478</p>
                        <p className="text-gray-700"><strong>Name:</strong> Muhammad Yousaf Iqbal</p>
                        <p className="text-gray-700"><strong>Amount:</strong> PKR {calculateTotal().toLocaleString()}</p>
                      </div>
                    </div>
                    <DocumentCheckIcon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Important Instructions */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-bold text-yellow-900 mb-2">📱 Important Instructions:</h4>
                <ol className="text-sm text-yellow-900 space-y-1 list-decimal list-inside">
                  <li>Transfer the payment using any of the methods above</li>
                  <li>Take a screenshot of the payment receipt/confirmation</li>
                  <li>Send the screenshot to WhatsApp: <strong>+923080323323</strong></li>
                  <li>Mention your email or business name in the message</li>
                  <li>Your subscription will be activated within 1-2 hours</li>
                </ol>
              </div>

              {/* Contact */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <PhoneIcon className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-green-900 mb-1">WhatsApp Support</h4>
                    <p className="text-sm text-green-800">
                      Send payment proof to: <strong>+923080323323</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Proof Upload Section */}
              <div className="border-t-2 border-gray-200 pt-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">📸 Upload Payment Proof</h4>
                <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center">
                  <input
                    type="file"
                    id="payment-proof"
                    accept="image/*,.pdf"
                    onChange={handlePaymentProofUpload}
                    className="hidden"
                  />
                  <label htmlFor="payment-proof" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <DocumentCheckIcon className="h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        {paymentProof ? (
                          <>
                            <span className="font-semibold text-green-600">✓ {paymentProof.name}</span>
                            <br />
                            <span className="text-xs text-gray-500">Click to change file</span>
                          </>
                        ) : (
                          <>
                            <span className="font-semibold text-blue-600">Click to upload</span>
                            <br />
                            <span className="text-xs text-gray-500">Screenshot of payment receipt (JPG, PNG, PDF max 5MB)</span>
                          </>
                        )}
                      </p>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Make sure the payment receipt clearly shows the transaction amount, date, and confirmation
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentProof(null);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-900 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPaymentProof}
                  disabled={!paymentProof || uploadingProof}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {uploadingProof ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Submit Payment Proof
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionRequestPage;
